<template>
  <div class="v2-cart-products">
    <!-- Loading -->
    <template v-if="!isLoaded">
      <div class="v2-flex v2-justify-center v2-p-8">
        <v2-spinner label="Loading products..." />
      </div>
    </template>

    <template v-else>
      <!-- Cart errors (retailer flow — shown only after place order attempt) -->
      <v2-cart-errors
        v-if="cartId && showErrors && isCartHasErrors"
        :cart-products="products"
        :cart-id="cartId"
        @on-close-cart-error-popup="(error) => $emit('on-cart-error', { action: 'close', error })"
        @on-click-cart-error-button="(error) => $emit('on-cart-error', { action: 'btn-click', error })"
      />

      <!-- Search + Cart action buttons row (hidden in multicart — outer search handles it) -->
      <div
        v-if="!isMultipleCart && (products.length || originalProducts.length)"
        class="v2-cart-products__search-row"
      >
        <div class="v2-cart-products__search">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
            />
            <line
              x1="21"
              y1="21"
              x2="16.65"
              y2="16.65"
            />
          </svg>
          <input
            v-model="localSearchText"
            placeholder="Search by products, style code..."
          >
        </div>
        <!-- Clear Cart / Save Cart (retailer non-popup context) -->
        <div
          v-if="cartId && !isPopup"
          class="v2-cart-products__cart-buttons"
        >
          <v2-button
            variant="destructive"
            style="border: 1px solid var(--v2-destructive)"
            @click="$emit('clear-cart')"
          >
            <shopping-bag-deletete-icon class="v2-cart-products__clear-icon" />
            Clear Cart
          </v2-button>
          <v2-button
            variant="outline"
            :disabled="isSaving"
            @click="onSaveCart"
          >
            {{ isSaving ? 'Saving...' : 'Save Cart' }}
          </v2-button>
        </div>
      </div>

      <!-- Zero qty error banner (brand flow only — retailer uses V2CartErrors instead) -->
      <div
        v-if="!cartId && showErrors && zeroQtyProducts.length"
        class="v2-cart-products__error-banner"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          style="flex-shrink: 0"
        >
          <circle
            cx="12"
            cy="12"
            r="10"
          />
          <line
            x1="12"
            y1="8"
            x2="12"
            y2="12"
          />
          <line
            x1="12"
            y1="16"
            x2="12.01"
            y2="16"
          />
        </svg>
        <div class="v2-cart-products__error-banner-text">
          <strong>{{ zeroQtyProducts.length }} product{{ zeroQtyProducts.length > 1 ? 's' : '' }} have zero quantity</strong>
          <span>Update quantities or remove them from the cart.</span>
        </div>
        <button
          class="v2-cart-products__error-remove-all"
          @click="handleRemoveZeroQtyProducts"
        >
          Remove all
        </button>
      </div>

      <!-- Decimal qty warning banner (brand flow only — retailer uses V2CartErrors instead) -->
      <div
        v-if="!cartId && showErrors && decimalQtyProducts.length"
        class="v2-cart-products__warning-banner"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          style="flex-shrink: 0"
        >
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line
            x1="12"
            y1="9"
            x2="12"
            y2="13"
          />
          <line
            x1="12"
            y1="17"
            x2="12.01"
            y2="17"
          />
        </svg>
        <div class="v2-cart-products__error-banner-text">
          <strong>{{ decimalQtyProducts.length }} product{{ decimalQtyProducts.length > 1 ? 's' : '' }} have decimal quantities</strong>
          <span>Update quantities to whole numbers.</span>
        </div>
      </div>

      <!-- Product cards
           Renders a paginated slice of displayedProducts (the FULL filtered
           list). All business logic — validation, search, remove-all-zero-
           qty, totals — operates on `products` (untouched, full cart). Only
           what gets mounted is paginated, starting with PRODUCTS_LIMIT (20)
           items and growing by another PRODUCTS_LIMIT each time the user
           scrolls near the bottom. Reset to PRODUCTS_LIMIT whenever the
           search query changes so a new filter doesn't inherit a large
           render window from the previous query. -->
      <template v-if="displayedProducts.length">
        <div
          v-for="product in renderedProducts"
          :key="product._id"
          class="v2-cart-products__card"
          :class="getCardErrorClasses(product)"
        >
          <!-- Per-product error banner (retailer context, shown after place order attempt) -->
          <div
            v-if="cartId && showErrors && getProductErrorText(product)"
            class="v2-cart-products__product-error-banner"
          >
            {{ getProductErrorText(product) }}
          </div>
          <div class="v2-cart-products__layout">
            <!-- Image column -->
            <div class="v2-cart-products__image">
              <router-link
                :to="{
                  name: 'v2-product-detail',
                  params: { id: product._id, collectionId: product.collectionId },
                }"
              >
                <img
                  :src="parseProductImage(product)"
                  :alt="product.name"
                  class="v2-cart-products__img"
                  @error="imageLoadError"
                >
              </router-link>
            </div>

            <!-- Info column -->
            <div class="v2-cart-products__info">
              <div class="v2-cart-products__header">
                <div>
                  <h4 class="v2-cart-products__name">
                    <router-link
                      :to="{
                        name: 'v2-product-detail',
                        params: { id: product._id, collectionId: product.collectionId },
                      }"
                      class="v2-cart-products__name-link"
                    >
                      {{ product.name }}
                    </router-link>
                  </h4>
                  <p class="v2-cart-products__breadcrumb">
                    {{ [product.department, product.category, product.subCategory].filter(Boolean).join(' / ') }}
                  </p>
                </div>
              </div>

              <div class="v2-cart-products__details">
                <div class="v2-cart-products__detail-row">
                  <span>Style Code:</span>
                  <strong>{{ product.styleCode }}</strong>
                </div>
                <!-- KP-4350: tags from the retailer's segment (e.g. MCA) -->
                <v2-segment-tags
                  :product="product"
                  inline
                  class="v2-cart-products__segment-tags"
                />
                <div class="v2-cart-products__detail-row">
                  <span>Retail Price:</span>
                  <strong>{{ formatCurrency(product.retailPrice, product.currency) }}</strong>
                </div>
                <div class="v2-cart-products__detail-row">
                  <span>Wholesale Selling Price:</span>
                  <strong>
                    <span
                      v-if="!product.discountedPrice"
                    >{{ formatCurrency(product.price, product.currency) }}</span>
                    <span
                      v-else
                      class="v2-cart-products__price-discounted"
                    >{{ formatCurrency(product.discountedPrice, product.currency) }}</span>
                  </strong>
                </div>
                <div
                  v-if="collectionEndDate"
                  class="v2-cart-products__detail-row"
                >
                  <span>Order Deadline:</span>
                  <strong>{{ formatDateValue(collectionEndDate) }}</strong>
                </div>
                <div
                  v-if="product.shippingStartDate && product.shippingEndDate"
                  class="v2-cart-products__detail-row"
                >
                  <span>Availability:</span>
                  <strong>{{ formattedDateValue(product.shippingStartDate) }} to {{ formattedDateValue(product.shippingEndDate) }}</strong>
                </div>
              </div>

              <!-- Carton quantity -->
              <div
                v-if="product.isCarton"
                class="v2-cart-products__carton-qty"
              >
                <span>Qty:</span>
                <input
                  v-model.number="product.quantity"
                  type="number"
                  class="v2-cart-products__qty-input"
                  :disabled="isProductValueDisabled(product.quantityAvailable)"
                  :max="getMaxQuantity(product.quantityAvailable)"
                  min="1"
                  @input="updateQuantity(product)"
                >
                <span
                  v-if="hasProductMaxQuantity(product.quantityAvailable)"
                  style="font-size: 0.75rem; color: var(--v2-muted-foreground)"
                >
                  Max Qty: {{ product.quantityAvailable }}
                </span>
              </div>

              <!-- Product Variants -->
              <product-variants
                :is-deleted="product.isDeleted"
                :is-carton="product.isCarton"
                :items="product.items"
                :is-edit-enabled="true"
                :min-qty-per-size="productMinQtyPerSize(product)"
                @update-variants="(variants) => handleVariantUpdate(variants, product)"
              />
            </div>

            <!-- Totals column -->
            <div class="v2-cart-products__totals-col">
              <!-- Action buttons -->
              <div class="v2-cart-products__action-buttons">
                <v2-button
                  v-if="cartId && !isMultipleCart"
                  variant="outline"
                  size="sm"
                  title="Replace Product"
                  @click="onClickReplace(product)"
                >
                  Replace
                </v2-button>
                <v2-button
                  variant="destructive"
                  size="sm"
                  style="border: 1px solid var(--v2-destructive)"
                  @click="onRemoveClick(product)"
                >
                  Remove
                </v2-button>
              </div>

              <!-- Totals -->
              <div
                v-if="variantLoaded"
                class="v2-cart-products__totals"
              >
                <div class="v2-cart-products__total-value">
                  Total Value:<br>
                  {{ formatCurrency(product.lineTotalPriceWithDiscount || product.lineTotalPrice || 0, product.currency) }}
                </div>
                <div class="v2-cart-products__total-units">
                  Total Units: {{ formatNumber(product.lineTotalQuantity || 0) }}
                </div>
                <div
                  v-if="minQtyErrorMessage(product)"
                  class="v2-cart-products__min-qty-error"
                >
                  {{ minQtyErrorMessage(product) }}
                </div>
                <div
                  v-if="segmentMaxQtyLabel(product)"
                  class="v2-cart-products__min-qty-error"
                  :class="{ 'v2-cart-products__min-qty-error--exceeded': isSegmentQuantityBlocked(product, product.lineTotalQuantity) }"
                >
                  {{ segmentMaxQtyLabel(product) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Infinite-scroll sentinel: when in view, grow the render limit
             by another PRODUCTS_LIMIT until the full filtered list is
             mounted. Stays hidden when there's nothing more to load. -->
        <div
          v-if="hasMoreToRender"
          ref="loadMoreSentinel"
          class="v2-cart-products__load-more"
        >
          <v2-spinner label="Loading more..." />
        </div>
      </template>

      <!-- Empty state — distinguish "no products at all" from
           "search returned nothing". products[] is the full cart;
           displayedProducts is what survives the (possibly empty) search
           filter. Gate + displayed query both use localSearchText (the
           input model) — not the debounced searchText — so the copy
           stays in sync with what the user typed. If we used searchText,
           clearing the input would still show "No products match <old>"
           for up to 250ms while the debounce caught up. -->
      <template v-else>
        <div class="v2-cart-products__empty">
          <template v-if="products.length && localSearchText && localSearchText.trim()">
            No products match "{{ localSearchText.trim() }}"
          </template>
          <template v-else>
            No products in cart
          </template>
        </div>
      </template>
    </template>

    <!-- Remove confirmation modal -->
    <v2-modal
      :show="!!showRemoveModal"
      title="Remove this product?"
      @close="showRemoveModal = null"
    >
      <p>Are you sure you want to delete this product?</p>
      <template #footer>
        <v2-button
          variant="outline"
          @click="showRemoveModal = null"
        >
          Dismiss
        </v2-button>
        <v2-button
          variant="destructive"
          @click="handleConfirmRemoval"
        >
          Remove product
        </v2-button>
      </template>
    </v2-modal>
  </div>
</template>

<style lang="scss" scoped>
// Cart-specific: ensure inputs are clickable even over overlays.
// Global ProductVariants table overrides live in _components.scss.
.v2-cart-products__info ::v-deep .product-variants input.form-control {
  pointer-events: auto;
}

.v2-cart-products__clear-icon {
  vertical-align: bottom;
}

.v2-cart-products__segment-tags {
  margin: 4px 0;
}

.v2-cart-products__search-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.v2-cart-products__cart-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.v2-cart-products__action-buttons {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.v2-cart-products__product-error-banner {
  background-color: #fef2f2;
  color: var(--v2-destructive);
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  padding: 0.375rem 1rem;
  border-radius: var(--v2-radius) var(--v2-radius) 0 0;
}

.v2-cart-products__card--cancelled,
.v2-cart-products__card--zero-qty,
.v2-cart-products__card--decimal {
  border: 2px solid var(--v2-destructive);
}

// Inter-card vertical gap — natural DOM flow (no virtualisation
// container), so margin-bottom works as expected.
.v2-cart-products__card {
  margin-bottom: var(--v2-space-3, 0.75rem);
}

// Infinite-scroll sentinel. Visible while there are more items in the
// filtered list than currently rendered; hidden once everything's mounted.
.v2-cart-products__load-more {
  display: flex;
  justify-content: center;
  padding: var(--v2-space-4, 1rem) 0;
}
</style>

<script>
import { debounce } from 'lodash'
import { PRODUCTS_LIMIT } from '@/constants'
import V2Button from '@/components/v2/V2Button.vue'
import V2Spinner from '@/components/v2/V2Spinner.vue'
import V2Modal from '@/components/v2/V2Modal.vue'
import ProductVariants from '@/views/apps/product/detail/ProductVariants.vue'
import { useCartProducts } from '@/composables/useCartProducts'
import { formatDate } from '@/@core/utils/filter'
import { utils } from '@kingpin-global/kingpin-utils-frontend'
import { UPDATE_CART_ERRORS } from '@/store/modules/checkout-v2.module'
import {
  getCartProductsErrorStatusText,
  isSegmentQuantityBlocked,
  productMinQtyPerSize,
  segmentMaxQtyLabel,
} from '@/views/apps/checkout/cart-products-utils'
import ShoppingBagDeleteteIcon from '@/@core/assets/svg-components/ShoppingBagDeleteteIcon.vue'
import V2SegmentTags from '@/components/v2/V2SegmentTags.vue'
import V2CartErrors from './V2CartErrors.vue'

const { formattedDate } = utils

export default {
  name: 'V2CartProducts',
  components: {
    V2SegmentTags,
    V2Button,
    V2Spinner,
    V2Modal,
    V2CartErrors,
    ShoppingBagDeleteteIcon,
    ProductVariants,
  },
  props: {
    cartProducts: {
      type: Array,
      default: () => [],
    },
    isPopup: {
      type: Boolean,
      default: false,
    },
    retailerId: {
      type: String,
      default: '',
    },
    modalId: {
      type: String,
      default: '',
    },
    cartId: {
      type: String,
      default: '',
    },
    cartEntityId: {
      type: String,
      default: '',
    },
    storeName: {
      type: String,
      default: '',
    },
    isMultipleCart: {
      type: Boolean,
      default: false,
    },
    // In multi-store mode the search input lives on the parent
    // V2RetailerCartList. The parent forwards its text here so the
    // composable's searchText ref drives v-show / displayedProducts
    // without touching cart.products (which would otherwise trigger
    // V2CartProducts' cartProducts watcher and remount every card).
    externalSearchText: {
      type: String,
      default: '',
    },
    collectionEndDate: {
      type: String,
      default: null,
    },
  },
  emits: [
    'update-total',
    'update-total-units',
    'has-cart-product',
    'remove-product-selected-from-brand',
    'update-notes',
    'remove-multiple-products',
    'on-cart-error',
    'on-product-remove',
    'on-change-product-count',
    'clear-cart',
  ],
  setup(props, { emit, root }) {
    const cartProducts = useCartProducts(props, emit, root)
    return { ...cartProducts }
  },
  data() {
    return {
      showRemoveModal: null,
      showErrors: false,
      forceNextReinit: false,
      isSaving: false,
      // Local input model — debounced into the composable's searchText so
      // typing on a 2k+ cart doesn't trigger one full filter+remount per
      // keystroke. searchText is what isProductMatch / displayedProducts
      // read, so the debounce gates ALL downstream re-render work.
      localSearchText: '',
      // Render pagination — only the first `renderLimit` items of the
      // filtered list are actually mounted. Grows by PRODUCTS_LIMIT each
      // time the load-more sentinel intersects the viewport. Resets to
      // PRODUCTS_LIMIT whenever search changes so a new filter doesn't
      // carry over a large render window from the previous query.
      renderLimit: PRODUCTS_LIMIT,
    }
  },
  computed: {
    zeroQtyProducts() {
      return this.products.filter(p => this.isProductHasZeroQty(p))
    },
    decimalQtyProducts() {
      return this.products.filter(p => this.isProductWithDecimalQty(p))
    },
    isCartHasErrors() {
      if (!this.cartId) return false
      return this.$store.state.checkout.isCartHasErrors > 0
    },
    // Full filtered list — search runs against the full cart so matches
    // never miss off-screen rows. Validation / zero-qty / select-all read
    // from `products` (not this). searchText is debounced (250ms via the
    // localSearchText watcher) so each keystroke doesn't re-filter.
    displayedProducts() {
      const query = (this.searchText || '').trim()
      if (!query) return this.products
      return this.products.filter(p => this.isProductMatch(p))
    },
    // Paginated slice actually rendered by v-for. Initial PRODUCTS_LIMIT
    // (20) items, grows by PRODUCTS_LIMIT each time the load-more sentinel
    // enters the viewport (see IntersectionObserver in mounted()).
    renderedProducts() {
      return this.displayedProducts.slice(0, this.renderLimit)
    },
    hasMoreToRender() {
      return this.renderLimit < this.displayedProducts.length
    },
  },
  watch: {
    externalSearchText: {
      immediate: true,
      handler(val) {
        // Mirror the parent-owned search text onto the composable's
        // searchText ref so the reactive v-show / displayedProducts
        // filtering picks it up. Multi-store cart pages drive search from
        // the top-level toolbar (which does its own rate-limiting), so we
        // sync without debounce here. The local input below has its own
        // debounce on `localSearchText`.
        this.searchText = val || ''
        this.localSearchText = val || ''
      },
    },
    localSearchText(val) {
      this.debouncedSyncSearchText(val)
    },
    // Reset render pagination whenever the (debounced) search query
    // changes — otherwise switching queries would inherit the previous
    // window and either over-mount on the first frame or under-show
    // matches.
    searchText() {
      this.renderLimit = PRODUCTS_LIMIT
    },
    cartProducts(newVal) {
      if (!newVal?.length) return
      // After import, forceNextReinit is set so we pick up new quantities
      // even though the product IDs haven't changed.
      if (!this.forceNextReinit) {
        // Skip re-init only when the product set is EXACTLY the same — same
        // size + same IDs. Covers:
        //   1. Retailer switch: same product IDs, fresh pricing → preserve user edits
        //   2. Local removal: composable already updated productsSlice, useBrandCart
        //      then splices props.cartProducts triggering this watcher — re-initing
        //      here would cause duplicates and undo the removal.
        // Re-init when new contains products the slice doesn't have (user added
        // more items in the source view) — the previous "every current in new"
        // check incorrectly bailed when new was a SUPERSET of current, hiding
        // the newly added products.
        const newIdSet = new Set(newVal.map(p => p._id))
        const sliceIdSet = new Set(this.productsSlice.map(p => p._id))
        // sliceIdSet.size > 0 guard prevents vacuous equality on first mount
        // (both empty) which would incorrectly skip the initial init.
        const sameSet = sliceIdSet.size > 0
          && sliceIdSet.size === newIdSet.size
          && [...sliceIdSet].every(id => newIdSet.has(id))
        if (sameSet) return
      }
      this.forceNextReinit = false
      // Full re-init
      this.productsSlice.splice(0, this.productsSlice.length)
      this.showErrors = false
      this.initializeWithCartProducts(newVal)
    },
  },
  // Per-instance debounced syncer — declared in created() (not methods{})
  // so each component instance gets its own timer. A method-level debounce
  // would share state across instances and would lose its trailing call
  // when one cart unmounts while another is still typing.
  created() {
    this.debouncedSyncSearchText = debounce(val => {
      this.searchText = val || ''
    }, 250)
  },
  mounted() {
    // IntersectionObserver-based load-more — fires when the sentinel
    // scrolls into view (50 px before, so the next page mounts a tick
    // before the user actually sees the bottom). Cheaper and more
    // accurate than a scroll-position listener; auto-handles the popup
    // and multi-store scroll contexts because the observer's default root
    // is the nearest scrollable ancestor.
    this.loadMoreObserver = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting) && this.hasMoreToRender) {
        this.renderLimit = Math.min(
          this.renderLimit + PRODUCTS_LIMIT,
          this.displayedProducts.length,
        )
      }
    }, { rootMargin: '50px' })
    this.observeLoadMoreSentinel()
  },
  updated() {
    // Sentinel is conditionally rendered, so its ref appears/disappears
    // across renders — re-attach the observer each tick to catch the case
    // where the previous load fully consumed the list (sentinel hidden)
    // and a new search restored it (sentinel back).
    this.observeLoadMoreSentinel()
  },
  beforeDestroy() {
    if (this.debouncedSyncSearchText?.cancel) {
      this.debouncedSyncSearchText.cancel()
    }
    if (this.loadMoreObserver) {
      this.loadMoreObserver.disconnect()
      this.loadMoreObserver = null
    }
  },
  methods: {
    onRemoveClick(product) {
      if (this.cartId) {
        this.$emit('on-product-remove', { action: 'click', productId: product._id })
      }
      this.showRemoveModal = product._id
    },
    handleConfirmRemoval() {
      const product = this.products.find(p => p._id === this.showRemoveModal)
      if (product) {
        if (this.cartId) {
          this.$emit('on-product-remove', { action: 'confirm', productId: product._id })
        }
        this.confirmProductRemoval(product)
      }
      this.showRemoveModal = null
    },
    activateErrors() {
      this.showErrors = true
    },
    scheduleReinitialize() {
      this.forceNextReinit = true
    },
    async onSaveCart() {
      this.isSaving = true
      try {
        await this.saveCartProducts()
      } finally {
        this.isSaving = false
      }
    },
    formatDateValue(date) {
      return formatDate(date)
    },
    formattedDateValue(date) {
      return formattedDate(date)
    },
    segmentMaxQtyLabel,
    isSegmentQuantityBlocked,
    productMinQtyPerSize,
    getCardErrorClasses(product) {
      if (!this.showErrors) return {}
      return {
        'v2-cart-products__card--error': this.isNotValidMinQty(product, product.lineTotalQuantity) || this.isProductHasZeroQty(product)
          || isSegmentQuantityBlocked(product, product.lineTotalQuantity),
        'v2-cart-products__card--cancelled': !!getCartProductsErrorStatusText(product),
        'v2-cart-products__card--decimal': this.isProductWithDecimalQty(product),
        'v2-cart-products__card--zero-qty': this.isProductHasZeroQty(product),
      }
    },
    getProductErrorText(product) {
      return getCartProductsErrorStatusText(product)
    },
    handleVariantUpdate(variants, product) {
      this.updateProductVariants(variants, product)
      if (this.cartId) {
        this.$store.commit(UPDATE_CART_ERRORS)
        this.$emit('on-change-product-count', product._id)
      }
    },
    handleRemoveZeroQtyProducts() {
      const ids = this.products
        .filter(p => this.isProductHasZeroQty(p))
        .map(p => p._id)
      this.removeMultipleProducts(ids)
    },
    // Re-bind the IntersectionObserver to the current sentinel element.
    // Safe to call repeatedly — observing the same element is a no-op;
    // observing a fresh element after re-render correctly restarts
    // intersection tracking.
    observeLoadMoreSentinel() {
      if (!this.loadMoreObserver) return
      const el = this.$refs.loadMoreSentinel
      if (el) this.loadMoreObserver.observe(el)
    },
  },
}
</script>
